import PathConstants from '@/main/routes/path-contants'
import { ValidAccountState } from '@/presentation/contexts/auth'
import useAccount from '@/presentation/hooks/auth/use-account'
import { AppBar, Box, Button, Container, IconButton, Slide, Toolbar } from '@mui/material'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { Analytics } from '../../analytics'
import theme from '@/presentation/assets/styles/theme'
import SxHeader from '../../sx-header'
import { Link } from 'react-router-dom'
import GlobalSearchField from '../../../input/global-search-field/global-search-field'
import { showUpgradeRequestButton } from '@/presentation/helpers/upgrade-request-helper'
import UpgradeIcon from '@mui/icons-material/Upgrade'
import AccountAvatarApp, { AccountAvatarSize } from '../../../data-display/account-avatar-app'
import DesktopUserMenu from './desktop-user-menu'
import logoIconUnicoSkill from '@images/logo-icon-unico-skill.svg'
import { i18nTranslationHelper } from '@/i18n'
import { showDependentRequestButton } from '@/presentation/helpers/dependent-request-helper'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'

interface HideOnScrollProps {
  trigger: boolean
  children: React.ReactElement
}

function HideOnScroll(props: HideOnScrollProps) {
  const { children, trigger } = props
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  )
}

type DesktopHeaderProps = {
  enableSearch: boolean
  trigger: boolean
  desktopNavMenu?: React.ReactElement
  menuOpen: boolean
  menuAnchorEl: null | HTMLElement
  handleOpenMenu: (event: React.MouseEvent<HTMLElement>) => void
  handleCloseMenu: () => void
}

// Desktop Header Component
export default function DesktopHeader({
  enableSearch,
  trigger,
  desktopNavMenu,
  menuOpen,
  menuAnchorEl,
  handleOpenMenu,
  handleCloseMenu
}: DesktopHeaderProps) {
  const t = i18nTranslationHelper.getTComponents
  const [account] = useAccount() as ValidAccountState
  const ldClient = useLDClient()
  const { HOME } = PathConstants.Routes

  const handleClickOnLogo = () => {
    Analytics.trackNavigate(HOME.name)
  }

  const handleResetDependentForm = () => {
    sessionStorage.removeItem('dependentRequestForm')
  }

  return (
    <Box className="only-desktop">
      {/* Main header bar */}
      <AppBar component="nav" sx={SxHeader.appBar(trigger, true)} position="fixed">
        <Toolbar
          sx={{
            height: theme.spacing(8),
            bgcolor: theme.palette.common.white
          }}
          className="border-0 border-b border-solid border-[color:var(--grey-50)]"
        >
          <Container
            disableGutters
            maxWidth="lg"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flex: 1,
                alignItems: 'center'
              }}
            >
              <Box
                component={Link}
                to={HOME.value}
                sx={{
                  height: theme.spacing(5),
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <img
                  src={logoIconUnicoSkill}
                  alt="Unico Skill"
                  className="cursor-pointer"
                  onClick={handleClickOnLogo}
                />
                <img
                  src={`${account.company.logoUrl}`}
                  alt={account.company.name}
                  className="h-[70%] ml-2 cursor-pointer"
                  onClick={handleClickOnLogo}
                />
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '480px',
                margin: '0 auto'
              }}
            >
              {enableSearch && <GlobalSearchField />}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flex: 1,
                alignItems: 'center'
              }}
            >
              {showUpgradeRequestButton(ldClient, account) && (
                <Button
                  startIcon={<UpgradeIcon />}
                  component={Link}
                  to={PathConstants.Routes.UPGRADE_REQUEST.value}
                  state={{ prevPath: location.pathname }}
                  sx={{
                    height: '100%',
                    fontSize: theme.spacing(1.75),
                    bgcolor: theme.palette.primary[25],
                    ml: theme.spacing(3 / 2),
                    px: theme.spacing(2),
                    borderRadius: Number.MAX_SAFE_INTEGER,
                    '&:hover': {
                      bgcolor: theme.palette.primary[50]
                    }
                  }}
                >
                  {t('desktopUpgradeRequestButton.cta')}
                </Button>
              )}

              {showDependentRequestButton(account) && (
                <Button
                  startIcon={<PersonAddAltOutlinedIcon />}
                  component={Link}
                  onClick={handleResetDependentForm}
                  to={PathConstants.Routes.DEPENDENT_REQUEST.value}
                  state={{ prevPath: location.pathname }}
                  sx={{
                    height: '100%',
                    fontSize: theme.spacing(1.75),
                    bgcolor: theme.palette.primary[25],
                    ml: theme.spacing(3 / 2),
                    px: theme.spacing(2),
                    borderRadius: Number.MAX_SAFE_INTEGER,
                    '&:hover': {
                      bgcolor: theme.palette.primary[50]
                    }
                  }}
                >
                  {t('desktopDependentRequestButton.cta')}
                </Button>
              )}

              <IconButton
                size="small"
                sx={{ ml: 3 / 2 }}
                aria-controls={menuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                onClick={handleOpenMenu}
              >
                <AccountAvatarApp size={AccountAvatarSize.small} />
              </IconButton>

              <DesktopUserMenu anchorEl={menuAnchorEl} handleClose={handleCloseMenu} />
            </Box>
          </Container>
        </Toolbar>
      </AppBar>

      {/* Navigation menu bar */}
      {desktopNavMenu && (
        <HideOnScroll trigger={trigger}>
          <AppBar
            component="nav"
            sx={{
              ...SxHeader.appBar(true),
              top: theme.spacing(8)
            }}
            position="fixed"
          >
            <Toolbar
              sx={{
                height: theme.spacing(8),
                alignItems: 'end',
                bgcolor: theme.palette.common.white
              }}
            >
              <Container disableGutters maxWidth="lg">
                {desktopNavMenu}
              </Container>
            </Toolbar>
          </AppBar>
        </HideOnScroll>
      )}
    </Box>
  )
}
